import { useEffect, useState, createElement, type ReactNode } from "react";
import { ArticleCodeBlock } from "@/components/articles/ArticleCodeBlock";

const ATTR_NAME_MAP: Record<string, string> = {
  class: "className",
  for: "htmlFor",
  tabindex: "tabIndex",
  colspan: "colSpan",
  rowspan: "rowSpan",
  readonly: "readOnly",
  maxlength: "maxLength",
  minlength: "minLength",
  autofocus: "autoFocus",
  enctype: "encType",
};

function elementToProps(el: Element): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const attr of Array.from(el.attributes)) {
    if (attr.name.startsWith("on")) continue;
    const reactName = ATTR_NAME_MAP[attr.name] ?? attr.name;
    props[reactName] = attr.value;
  }
  return props;
}

const TABLE_CONTAINER_TAGS = new Set([
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "colgroup",
]);

function nodeToReact(node: Node, key: string, parentTag?: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    if (parentTag && TABLE_CONTAINER_TAGS.has(parentTag) && !node.textContent?.trim()) {
      return null;
    }
    return node.textContent;
  }
  // Comment, CDATA, and processing-instruction nodes have no React equivalent.
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  if (
    tag === "pre" &&
    el.children.length === 1 &&
    el.children[0].tagName === "CODE"
  ) {
    const codeEl = el.children[0] as Element;
    const language = codeEl.className.trim().split(/\s+/)[0] || "plaintext";
    // Redcarpet appends a single trailing newline after the closing fence
    const code = codeEl.textContent.replace(/\n$/, "");
    return createElement(ArticleCodeBlock, { key, code, language });
  }

  const props = elementToProps(el);
  props.key = key;

  const children = Array.from(el.childNodes).map((child, i) =>
    nodeToReact(child, `${key}.${i}`, tag),
  );
  return createElement(tag, props, ...children);
}

export function parseArticleHtml(html: string): ReactNode | null {
  if (typeof DOMParser === "undefined") return null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return Array.from(doc.body.childNodes).map((child, i) =>
    nodeToReact(child, `n${i}`),
  );
}

// useState + useEffect (not useMemo) is required for SSR hydration safety:
// DOMParser is unavailable on the server, so the first render on both server
// and client must return null. The parsed tree is installed after hydration.
export function useArticleContent(contentHtml: string): ReactNode | null {
  const [parsed, setParsed] = useState<ReactNode | null>(null);

  useEffect(() => {
    setParsed(parseArticleHtml(contentHtml));
  }, [contentHtml]);

  return parsed;
}
